import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { AbstractSecurityStorage, OidcSecurityService, OpenIdConfiguration, provideAuth } from 'angular-auth-oidc-client';
import { environment } from '@environments/environment';
import { checkAuthOnStart, TabAwareSecurityStorage } from './tab-aware-auth';

/**
 * Вход через Keycloak (realm sport), клиент nok-spa: Authorization Code + PKCE, обновление по refresh-токену.
 * Токен подставляется в запросы к API (secureRoutes) интерцептором authInterceptor библиотеки.
 * Выход — через портал: Keycloak после logout возвращает на /frontchannel-logout портала,
 * а сам сервис получает front-channel logout на свой маршрут /frontchannel-logout.
 * Токены — в localStorage, как во всех сервисах (решение 01.10.2026, #29256): хранилище общее
 * для вкладок, новая вкладка сразу видит вход, а выход виден во всех вкладках.
 */
export function provideKeycloakAuth(): EnvironmentProviders {
    const config: OpenIdConfiguration = {
        authority: environment.keycloak.authority,
        clientId: environment.keycloak.clientId,
        scope: environment.keycloak.scope,
        responseType: 'code',
        redirectUrl: window.location.origin,
        postLogoutRedirectUri: new URL('frontchannel-logout', environment.portalUrl).toString(),
        silentRenew: true,
        useRefreshToken: true,
        renewTimeBeforeTokenExpiresInSeconds: 30,
        secureRoutes: [environment.apiUrl],
        unauthorizedRoute: '/unauthorized',
        forbiddenRoute: '/forbidden',
    };

    return makeEnvironmentProviders([
        provideAuth({ config }),
        // provideAuth по умолчанию регистрирует sessionStorage (у каждой вкладки свои токены);
        // этот провайдер идёт после него: токены — в localStorage, а вход и обновление токена,
        // идущие во вкладке, — в её sessionStorage, иначе вкладки сбивают друг другу state (#29300)
        { provide: AbstractSecurityStorage, useClass: TabAwareSecurityStorage },
        // Вход проверяется до старта приложения, как с withAppInitializerAuthCheck(); при истёкшем
        // access-токене — обновление по refresh-токену, без перехода на Keycloak
        provideAppInitializer(() => checkAuthOnStart(inject(OidcSecurityService))),
    ]);
}
